import { useMemo } from 'react'

import { bridgeRequest } from 'common'
import { TEndpoint, TEntity } from 'contexts/app-context/types'
import { isEmpty } from 'lodash'
import { TAcceptTermsBody } from 'pages/privacy-policy/types/useRequestTerms.types'

import { useMutation } from '@tanstack/react-query'

type Response = {
  headers: Headers
  httpStatus: number
  response: string
  timeSpent: number
}

const validateSuccessCode = (status?: number) => (status ? status >= 200 && status < 300 : false)

export const useAcceptTerms = (acceptTermsPayload?: TAcceptTermsBody, path?: string) => {
  const endpoint: TEndpoint | undefined = useMemo(() => {
    if (!path) return undefined

    return {
      id: useAcceptTerms.name,
      url: path,
      method: 'POST',
      isActive: true,
    }
  }, [path])

  const {
    mutateAsync: acceptTermsFn,
    isPending: isAcceptTermsLoading,
    isError,
    isSuccess,
    data,
  } = useMutation({
    mutationFn: async (org?: TEntity) => {
      if (!endpoint) throw new Error('Privacy policy accept terms endpoint is not available')
      if (isEmpty(acceptTermsPayload))
        throw new Error('Privacy policy accept terms request body not found')

      const response = await bridgeRequest<Response>({
        endpoint,
        body: acceptTermsPayload,
        returnAllResponse: true,
        isUSBranch: org === 'US_BRANCH',
      })

      return response
    },
  })

  const isAcceptTermsSuccess = useMemo(() => {
    if (data && validateSuccessCode(data.httpStatus)) return true
    return isSuccess
  }, [isSuccess, data])

  const isAcceptTermsError = useMemo(() => {
    if (data && validateSuccessCode(data.httpStatus)) return false
    return isError
  }, [isError, data])

  return {
    acceptTermsFn,
    isAcceptTermsLoading,
    isAcceptTermsError,
    isAcceptTermsSuccess,
  }
}
